import { atom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import { CENTRAL_API_V3 } from '@studio/constants/env-variables';
import { calculateTokenExpiration } from '@preview/helpers/calculateTokenExpiration';

export type AuthState = {
  error: Error | null;
  isExpired: boolean;
  isLoading: boolean;
  isRefreshing: boolean;
  refreshTokenError: Error | null;
  token: string | null;
  tokenExpiresAt: number | null;
};

export const authStateAtom = atomWithStorage<AuthState>('resi-site-preview-key', {
  token: null,
  tokenExpiresAt: null,
  isLoading: false,
  isExpired: false,
  isRefreshing: false,
  error: null,
  refreshTokenError: null,
});

export const loginAtom = atom(
  null,
  async (get, set, payload: { customerId?: string; password: string; username: string }) => {
    const tokenUrl = `${CENTRAL_API_V3}/auth/token`;

    try {
      const currentState = get(authStateAtom);
      set(authStateAtom, { ...currentState, isLoading: true, error: null });

      const response = await fetch(tokenUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          username: payload.username,
          password: payload.password,
          grant_type: 'password_cookie',
        }),
      });

      if (!response.ok) {
        throw new Error(`Login failed: ${response.status}`);
      }

      const data = await response.json();
      const tokenExpiresAt = calculateTokenExpiration(data.expires_in);

      set(authStateAtom, {
        ...currentState,
        tokenExpiresAt: tokenExpiresAt,
        token: data.access_token,
        isLoading: false,
        isExpired: false,
        error: null,
      });

      return { success: true, response: data };
    } catch (error) {
      console.error('Login error:', error);
      const currentState = get(authStateAtom);
      set(authStateAtom, {
        ...currentState,
        error: error instanceof Error ? error : new Error('Login failed'),
        isLoading: false,
      });
      return { success: false, error };
    }
  }
);

export const logoutAtom = atom(
  null,
  async (
    get,
    set,
    callbacks?: {
      onError?: () => void;
      onFinally?: () => void;
      onSuccess?: () => void;
    }
  ) => {
    try {
      const currentState = get(authStateAtom);
      const url = `${CENTRAL_API_V3}/logout`;

      await fetch(url, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${currentState.token}`,
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      // Clear auth state
      set(authStateAtom, {
        token: null,
        tokenExpiresAt: null,
        isLoading: false,
        isRefreshing: false,
        isExpired: false,
        error: null,
        refreshTokenError: null,
      });

      callbacks?.onSuccess?.();
    } catch (error) {
      console.error('Logout error:', error);
      callbacks?.onError?.();
    } finally {
      callbacks?.onFinally?.();
    }
  }
);

// Derived atoms for the complex state
export const errorAtom = atom((get) => get(authStateAtom).error);
export const refreshTokenErrorAtom = atom((get) => get(authStateAtom).refreshTokenError);
export const isLoadingAtom = atom((get) => get(authStateAtom).isLoading);
export const isExpiredAtom = atom((get) => get(authStateAtom).isExpired);
export const isRefreshingAtom = atom((get) => get(authStateAtom).isRefreshing);

export const isAuthenticatedAtom = atom((get) => {
  const authState = get(authStateAtom);
  const isTokenExpired = get(isTokenExpiredAtom);

  return authState.token !== null && !isTokenExpired && !authState.isExpired;
});

export const isTokenExpiredAtom = atom((get) => {
  const authState = get(authStateAtom);

  if (!authState.token || !authState.tokenExpiresAt) {
    return true;
  }

  const now = Date.now();
  const isExpired = now >= authState.tokenExpiresAt;

  return isExpired;
});

// Actions for the complex state
export const setAuthRefreshingAtom = atom(null, (get, set, refreshing: boolean) => {
  const currentState = get(authStateAtom);
  set(authStateAtom, { ...currentState, isRefreshing: refreshing });
});

export const setCompleteAuthStateAtom = atom(null, (get, set, newState: Partial<AuthState>) => {
  const currentState = get(authStateAtom);
  set(authStateAtom, { ...currentState, ...newState });
});

export const clearAuthAtom = atom(null, (get, set) => {
  set(authStateAtom, {
    token: null,
    tokenExpiresAt: null,
    isLoading: false,
    isExpired: false,
    isRefreshing: false,
    error: null,
    refreshTokenError: null,
  });
});
